import { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { notFound, useNavigate } from '@tanstack/react-router'
import { useDebounce } from 'commons/hooks/common'
import { QRWebPagePayload, QuarterlyReportWebPage } from 'commons/types/DTO/bank/quarterlyReport'
import { Questionnaire } from 'commons/types/DTO/bank/quarterlyReport/questionnaire'
import { Request } from 'commons/utils/request'
import { TablePagination, Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'
import { QR } from '~bank-quarterly-report/hooks'
import { SaveNotesRequest } from '~bank-quarterly-report/types'

import { handlerServerError } from '@orbit/errors'

import { useDeleteQR } from './useDeleteQR'

const QRConfig = {
  UPLOAD_FILES_LIMIT: 20,
  UPLOAD_FILES_OFFSET: 0,
  HISTORY_LIMIT: 10,
  HISTORY_OFFSET: 0
}

export const useQRDetails = (quarterlyAnalyticsId: number) => {
  const navigate = useNavigate()

  const defaultFilter: QRWebPagePayload = useMemo(
    () => ({
      quarterlyAnalyticsId: +quarterlyAnalyticsId,
      uploadedFilesLimit: QRConfig.UPLOAD_FILES_LIMIT,
      uploadedFilesOffset: QRConfig.UPLOAD_FILES_OFFSET,
      uploadedFilesSortBy: undefined,
      uploadedFilesSortDesc: true,
      historyLimit: QRConfig.HISTORY_LIMIT,
      historyOffset: QRConfig.HISTORY_OFFSET
    }),
    [quarterlyAnalyticsId]
  )

  const uploadedFilesTableState = useTableState({
    filter: {},
    order: [{ field: 'name', direction: 'DESC' }],
    page: 1,
    size: QRConfig.UPLOAD_FILES_LIMIT
  })

  const [webPageData, setWebPageData] = useState<QuarterlyReportWebPage | undefined>(undefined)
  const [filter, setFilter] = useState<QRWebPagePayload>(defaultFilter)

  const { data, isFetching, isFetched, refetch } = QR.useGetQRWebPage(filter)
  const invalidate = useCallback(async () => {
    await refetch()
  }, [refetch])

  useEffect(() => {
    if (isFetched && !data) {
      throw notFound()
    }
  })

  useEffect(() => {
    if (isFetched) {
      setWebPageData(data)
    }
  }, [isFetched, data])

  const changePagination = useCallback(
    async (page: number, size: number) => {
      uploadedFilesTableState.onPageChange(page, size)

      const filter = {
        ...defaultFilter,
        uploadedFilesOffset: (page - 1) * size,
        uploadedFilesLimit: size
      }

      setFilter(filter)
    },
    [defaultFilter, uploadedFilesTableState]
  )

  const [monthlyOpen, setMonthlyOpen] = useState(false)

  const { mutate: saveNotes } = useMutation<void, Error, SaveNotesRequest>({
    mutationFn: (opts) => Request.put(Request.urls.bank.reportQuarterlySaveNotes, opts),
    onSuccess: async () => {
      await invalidate()
      Toast.successSmall('Notes have been saved.')
    },
    onError: (error) => handlerServerError(error)
  })

  const { deleteQR } = useDeleteQR({
    onSuccess: async () => {
      if (webPageData?.infoRelationshipId) {
        navigate({ to: '/relationships/$id', params: { id: webPageData?.infoRelationshipId.toString() } })
      }
    },
    onError: (e) => handlerServerError(e)
  })

  const { mutate: reviewQR } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.reportQuarterlyReview, opts),
    onSuccess: async () => {
      await invalidate()
    },
    onError: (e) => handlerServerError(e)
  })

  const { mutate: updateQuestionnaire } = useMutation<void, Error, { id: number; questionnaire: Questionnaire }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.quarterlyQuestionnaire, opts),
    onSuccess: async () => {
      Toast.successSmallUpdatable('Questionnaire was saved')
    },
    onError: (error) => handlerServerError(error)
  })

  const handleSetQuestionnaire = useCallback(
    (newQuestionnaire: Questionnaire) => {
      if (webPageData) {
        updateQuestionnaire({
          id: webPageData?.quarterlyAnalyticsId,
          questionnaire: newQuestionnaire
        })
      }
    },
    [updateQuestionnaire, webPageData]
  )

  const onSetQuestionnaire = useDebounce(handleSetQuestionnaire, 1000) // MP-7568

  const { mutate: resolveWarnings } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.put(Request.urls.bank.reportQuarterlyAcknowledgeWarnings, opts),
    onSuccess: async () => {
      await invalidate()
      Toast.successSmall('All warnings have been resolved.')
    },
    onError: (error) => handlerServerError(error)
  })

  const { open: openDelete, Confirmation: DeleteConfirmation } = useConfirmation({
    dialogId: 'qr-delete-confirmation',
    message: `Are you sure you want to remove this report?`,
    onConfirm: () => deleteQR({ id: filter.quarterlyAnalyticsId }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const { open: openWarnings, Confirmation: WarningsConfirmation } = useConfirmation({
    dialogId: 'qr-warnings-confirmation',
    message: `Are you sure you want to acknowledge all warnings?`,
    onConfirm: () => resolveWarnings({ id: filter.quarterlyAnalyticsId }),
    confirmationButtonText: 'Confirm',
    isConfirmationNegative: false
  })

  const { open: openReview, Confirmation: ReviewConfirmation } = useConfirmation({
    dialogId: 'qr-review-confirmation',
    message: `Are you sure you want to review this report?`,
    onConfirm: () => reviewQR({ id: filter.quarterlyAnalyticsId }),
    confirmationButtonText: 'Review',
    isConfirmationNegative: false
  })

  const historyPagination: TablePagination = useMemo(
    () => ({
      total: data?.historyTotalCount,
      page: filter.historyOffset / filter.historyLimit + 1,
      size: filter.historyLimit,
      onChange: (page: number, size: number) =>
        setFilter({ ...filter, historyOffset: (page - 1) * size, historyLimit: size })
    }),
    [filter, data]
  )

  return {
    webPageData,
    isFetching,
    uploadedFilesTableState,
    uploadedFilesChangePagination: changePagination,
    openReview,
    ReviewConfirmation,
    openWarnings,
    WarningsConfirmation,
    openDelete,
    DeleteConfirmation,
    onSetQuestionnaire,
    saveNotes,
    monthlyOpen,
    setMonthlyOpen,
    historyPagination,
    invalidate
  }
}
